import React, { useState } from 'react';
import { Download, Users } from 'lucide-react';
import { useEmailSubscribers } from '../../../hooks/useEmailAdmin';
import emailApi from '../../../services/emailApi';
import SubscribersTable from './SubscribersTable';

export default function SubscribersManagement() {
  const [filters, setFilters] = useState({
    isActive: 'all',
    digestType: 'all',
    category: 'all',
    page: '1',
    limit: '50',
  });

  const { 
    subscribers, 
    total, 
    loading, 
    error, 
    deleteSubscriber,
    exportSubscribers,
  } = useEmailSubscribers(filters);

  const sendTest = async (email: string, id?: number) => {
    try {
      if (id) {
        await emailApi.sendInstantTest(id, 3);
      } else {
        await emailApi.sendTestEmail(email, 'instant_alert');
      }
      window.alert(`Test alert queued for ${email}`);
    } catch (e: any) {
      window.alert(`Failed to send test: ${e?.message || e}`);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: '1' }));
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-md">
        Error: {error}
      </div>
    );
  }

  const currentPage = parseInt(filters.page);
  const totalPages = Math.ceil(total / parseInt(filters.limit));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Users className="h-5 w-5" />
          Email Subscribers ({total})
        </h2>
        <button
          onClick={exportSubscribers}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select 
            value={filters.isActive} 
            onChange={(e) => handleFilterChange('isActive', e.target.value)}
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          >
            <option value="all">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Digest Type</label>
          <select 
            value={filters.digestType} 
            onChange={(e) => handleFilterChange('digestType', e.target.value)}
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          >
            <option value="all">All</option>
            <option value="INSTANT">Instant</option>
            <option value="DAILY">Daily</option>
            <option value="WEEKLY">Weekly</option>
            <option value="PAUSED">Paused</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Category</label>
          <select 
            value={filters.category} 
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          >
            <option value="all">All</option>
            <option value="MILITARY">Military</option>
            <option value="CYBER">Cyber</option>
            <option value="DIPLOMATIC">Diplomatic</option>
            <option value="ECONOMIC">Economic</option>
            <option value="INFORMATION_WARFARE">Information Warfare</option>
          </select>
        </div>
      </div>

      {/* Subscribers Table */}
      <SubscribersTable 
        subscribers={subscribers}
        onDelete={deleteSubscriber}
        onSendTest={(email) => {
          const s = subscribers.find(x => x.email === email);
          return sendTest(email, s?.id);
        }}
        loading={loading}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => handleFilterChange('page', String(currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handleFilterChange('page', String(currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="tc-card p-4 text-center">
          <div className="text-2xl font-bold tc-text-primary">
            {subscribers.filter(s => s.isActive).length}
          </div>
          <div className="text-sm tc-text-secondary">Active Subscribers</div>
        </div>
        <div className="tc-card p-4 text-center">
          <div className="text-2xl font-bold tc-text-primary">
            {subscribers.filter(s => s.digestType === 'DAILY').length}
          </div>
          <div className="text-sm tc-text-secondary">Daily Digest</div>
        </div>
        <div className="tc-card p-4 text-center">
          <div className="text-2xl font-bold tc-text-primary">
            {subscribers.filter(s => s.digestType === 'WEEKLY').length}
          </div>
          <div className="text-sm tc-text-secondary">Weekly Digest</div>
        </div>
        <div className="tc-card p-4 text-center">
          <div className="text-2xl font-bold tc-text-primary">
            {subscribers.filter(s => s.digestType === 'INSTANT').length}
          </div>
          <div className="text-sm tc-text-secondary">Instant Alerts</div>
        </div>
      </div>
    </div>
  );
}
